import { Video as VideoUi, VideoStyles } from "@business-finland/wif-ui-lib";
import { useRouter } from "next/router";
import DOMPurify from "isomorphic-dompurify";

import { getAssetPath } from "../../../util/magnolia";
import {
  generateVideoObjectSchema,
  videoSchemaToJsonLd,
} from "../../../service/schemaOrg/generators/video";

export interface IVideoProps {
  title: string;
  caption?: string;
  videoType: "asset" | "youtube" | "icareus";
  autoplay: boolean;
  className?: string;
  video: Wif.Mgnl.Video;
  image?: Wif.Mgnl.Image;
  imageAlt?: string;
  youtubeVideoUrl?: string; // Example: "https://www.youtube.com/watch?v=Wn1nf6eSAwg"
  icareus?: string;
}

function ensureIframeTitle(embed: string, title: string) {
  return embed.replace("title=\"\"", `title="${title}"`);
}

export function Video({
  title,
  caption,
  autoplay,
  image,
  imageAlt,
  videoType,
  video,
  youtubeVideoUrl,
  icareus,
}: IVideoProps): JSX.Element | null {
  const { isPreview } = useRouter();
  let derivedVideoType = "";
  let videoSrc = "";
  let imgSrc = "";
  let mimeType = "";
  let embedCode = "";

  if (videoType === "asset") {
    derivedVideoType = "asset";
    videoSrc = getAssetPath(video, isPreview);
    mimeType = video.metadata?.mimeType ?? "";
  }

  if (image) {
    imgSrc = getAssetPath(image, isPreview);
  }

  if (videoType === "youtube") {
    if (!youtubeVideoUrl) {
      return null;
    }

    derivedVideoType = "embed";
    embedCode = `
      <iframe
        src="${youtubeVideoUrl.replace("watch?v=", "embed/")}"
        title="${title || "Youtube video"}"
        allowfullscreen
        enablejsapi
      ></iframe>
    `;
  }

  const sanitizedData = () =>
    DOMPurify.sanitize(icareus as string, {
      ADD_TAGS: ["iframe"],
      ADD_ATTR: [
        "allow",
        "autoplay",
        "allowfullscreen",
        "frameborder",
        "scrolling",
      ],
    });

  if (videoType === "icareus") {
    derivedVideoType = "icareusIframe";
    embedCode = sanitizedData();
    embedCode = ensureIframeTitle(embedCode || "", title || "Embedded video");
  }

  // Generate video schema JSON-LD
  const videoSchema = generateVideoObjectSchema({
    title,
    caption,
    videoType,
    video,
    image,
    youtubeVideoUrl,
    icareus,
  });
  const videoSchemaJsonLd = videoSchemaToJsonLd(videoSchema);

  return (
    <>
      {videoSchemaJsonLd && (
        <script
          type="application/ld+json"
          nonce={process.env.NEXT_PUBLIC_SCRIPT_NONCE}
          dangerouslySetInnerHTML={{ __html: videoSchemaJsonLd }}
        />
      )}
      <VideoUi
        title={imageAlt && imgSrc ? imageAlt : title}
        caption={caption}
        image={imgSrc}
        video={{ src: videoSrc, mimeType }}
        autoplay={autoplay}
        embed={embedCode}
        videoType={derivedVideoType}
        styling={VideoStyles.ARTICLE}
      />
    </>
  );
}
